// src/v3/ui/backtester/backtester-page.tsx

import { Copy, Edit3, FolderOpen, Play, Plus, Save, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  formatDisplayDateTime,
  Loading,
  Progress,
  Select,
} from "../_common";
import {
  COMMISSION_TYPE_OBJ,
  SIZING_MODE,
  SIZING_MODE_OBJ,
  SLIPPAGE_TYPE_OBJ,
  STRATEGY_SOURCE_OBJ,
} from "../_deps";
import { t } from "../locales";
import {
  mapDetailToViewResult,
  useBacktestRunDetail,
  useBacktestRuns,
  useBacktestStrategies,
  useBacktester,
  useCloneStrategy,
  useCreateBacktestRun,
  useCreateStrategy,
  useDeleteBacktestRun,
  useDeleteStrategy,
  useGenerateStrategyCode,
  useUpdateStrategy,
  type BacktestResult,
  type StrategyInfo,
} from "./backtester.hooks";
import { ComparisonTable } from "./comparison-table";
import { OptimizationResults, ResultsViewer } from "./results-viewer";
import { StrategyEditorModal } from "./strategy-editor";

const PROGRESS_RESET_DELAY_MS = 2_000;

interface ParamGridRow {
  name: string;
  min: string;
  max: string;
  step: string;
}

export function BacktesterPage() {
  // ── Server-state queries (TanStack Query) ────────────────────────────────
  const { strategies, isLoading: strategiesLoading, error: strategiesError } =
    useBacktestStrategies();
  const { runs, isLoading: runsLoading, error: runsError } = useBacktestRuns();

  // ── Strategy mutations ────────────────────────────────────────────────────
  const { createStrategy } = useCreateStrategy();
  const { updateStrategy } = useUpdateStrategy();
  const { deleteStrategy } = useDeleteStrategy();
  const { cloneStrategy } = useCloneStrategy();
  const { generateStrategyCode } = useGenerateStrategyCode();

  // ── Sync RPC view-state (results from POST /run — not persisted) ──────────
  const { results, isLoading, error, runTest, optimize, clearResults } =
    useBacktester();

  // ── Persisted run mutations ───────────────────────────────────────────────
  const createRunMutation = useCreateBacktestRun();
  const deleteRunMutation = useDeleteBacktestRun();

  // ── Run detail (for opening past runs) ────────────────────────────────────
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const { detail, isLoading: detailLoading, error: detailError } =
    useBacktestRunDetail(selectedRunId);

  // ── View-state (UI flags, forms) ──────────────────────────────────────────
  const [strategy, setStrategy] = useState("pinbar-reversal");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<StrategyInfo | null>(
    null,
  );
  const [cloningStrategy, setCloningStrategy] = useState<StrategyInfo | null>(
    null,
  );
  const [initialCapital, setInitialCapital] = useState("10000");
  const [maxHoldBars, setMaxHoldBars] = useState("20");
  const [commission, setCommission] = useState("0.001");
  const [slippage, setSlippage] = useState("0.001");
  const [progress, setProgress] = useState<number | null>(null);
  const [symbols, setSymbols] = useState<string[]>(["AAPL"]);
  const [symbolsInput, setSymbolsInput] = useState("AAPL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sizingMode, setSizingMode] = useState<(typeof SIZING_MODE)[number]>(
    SIZING_MODE_OBJ.FIXED,
  );
  const [optimizationResult, setOptimizationResult] = useState<unknown | null>(
    null,
  );
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [paramGridRows, setParamGridRows] = useState<ParamGridRow[]>([
    { name: "maxHoldBars", min: "10", max: "30", step: "10" },
  ]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const dateRangeValid = !dateFrom || !dateTo || dateFrom <= dateTo;

  const handleRun = async () => {
    setProgress(0);
    await runTest(strategy, {
      initialCapital: Number(initialCapital),
      maxHoldBars: Number(maxHoldBars),
      commission: {
        type: COMMISSION_TYPE_OBJ.PERCENT,
        value: Number(commission),
      },
      slippage: { type: SLIPPAGE_TYPE_OBJ.PERCENT, value: Number(slippage) },
    });
    setProgress(100);
    setTimeout(() => setProgress(null), PROGRESS_RESET_DELAY_MS);
  };

  const handleSaveRun = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await createRunMutation.mutateAsync({
        strategy,
        symbols,
        params: {
          initialCapital: Number(initialCapital),
          maxHoldBars: Number(maxHoldBars),
          commission: {
            type: COMMISSION_TYPE_OBJ.PERCENT,
            value: Number(commission),
          },
          slippage: {
            type: SLIPPAGE_TYPE_OBJ.PERCENT,
            value: Number(slippage),
          },
        },
      });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save run");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRun = async (id: string) => {
    try {
      await deleteRunMutation.mutateAsync({ id });
      if (selectedRunId === id) setSelectedRunId(null);
    } catch {
      // error is captured in mutation state
    }
  };

  const handleAddSymbol = () => {
    const sym = symbolsInput.trim().toUpperCase();
    if (sym && !symbols.includes(sym)) {
      setSymbols([...symbols, sym]);
    }
  };

  const handleRemoveSymbol = (sym: string) => {
    setSymbols(symbols.filter((s) => s !== sym));
  };

  const handleOptimize = async () => {
    if (symbols.length === 0) return;
    if (!dateRangeValid) return;
    const paramGrid: Record<string, Array<number | string>> = {};
    for (const row of paramGridRows) {
      const name = row.name.trim();
      if (!name) continue;
      const min = Number(row.min);
      const max = Number(row.max);
      const step = Number(row.step);
      if (
        !Number.isFinite(min) ||
        !Number.isFinite(max) ||
        !Number.isFinite(step)
      ) {
        setOptimizeError(t("backtester.params.invalidNumbers", { name }));
        return;
      }
      if (step <= 0) {
        setOptimizeError(t("backtester.params.invalidStep", { name }));
        return;
      }
      const values: number[] = [];
      for (let v = min; v <= max + 1e-9; v += step) {
        values.push(Number(v.toFixed(6)));
      }
      paramGrid[name] = values;
    }
    if (Object.keys(paramGrid).length === 0) {
      setOptimizeError(t("backtester.params.empty"));
      return;
    }
    setIsOptimizing(true);
    setOptimizeError(null);
    try {
      const result = await optimize({
        strategy,
        symbols,
        paramGrid,
        initialCapital: Number(initialCapital),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        sizingMode,
      });
      setOptimizationResult(result);
    } catch (e) {
      setOptimizeError(e instanceof Error ? e.message : "Optimization failed");
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleAddParamRow = () => {
    setParamGridRows((prev) => [
      ...prev,
      { name: "", min: "", max: "", step: "" },
    ]);
  };

  const handleRemoveParamRow = (idx: number) => {
    setParamGridRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateParamRow = (
    idx: number,
    field: keyof ParamGridRow,
    value: string,
  ) => {
    setParamGridRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
  };

  // Map persisted run detail to view-state BacktestResult for ResultsViewer.
  const persistedResult: BacktestResult | null = detail
    ? mapDetailToViewResult(detail)
    : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("backtester.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {strategiesLoading && <Loading />}
          {strategiesError !== null && <ErrorState message={strategiesError} />}
          <div>
            <label className="mb-1 block text-sm text-muted-foreground">
              {t("backtester.strategy")}
            </label>
            <Select
              value={strategy}
              onChange={(v) => setStrategy(v)}
              ariaLabel={t("backtester.strategy")}
              className="w-full"
              options={strategies.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
            />
            {strategies.length > 0 && (
              <div className="mt-2 space-y-1">
                {strategies
                  .filter((s) => s.id === strategy)
                  .map((s) => (
                    <div
                      key={s.id}
                      className="rounded-md border bg-muted/30 p-2 text-xs"
                    >
                      <div className="font-medium">{s.name}</div>
                      <div className="text-muted-foreground">
                        {s.description}
                      </div>
                      <div className="text-muted-foreground">
                        {t("backtester.version")}: {s.version}
                      </div>
                    </div>
                  ))}
              </div>
            )}
            <div className="mt-2 rounded-md border border-dashed p-2 text-xs text-muted-foreground">
              {t("backtester.addStrategyHint")}
            </div>
            {/* REQ-8/9 (v1.1): управление стратегиями */}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingStrategy(null);
                  setCloningStrategy(null);
                  setEditorOpen(true);
                }}
              >
                <Plus className="h-3 w-3 mr-1" />
                New Strategy
              </Button>
              {strategies.find((s) => s.id === strategy) && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const s = strategies.find((x) => x.id === strategy);
                      if (!s) return;
                      if (s.source === STRATEGY_SOURCE_OBJ.USER) {
                        setEditingStrategy(s);
                        setCloningStrategy(null);
                      } else {
                        // builtin — клонирование.
                        setEditingStrategy(null);
                        setCloningStrategy(s);
                      }
                      setEditorOpen(true);
                    }}
                  >
                    {strategies.find((s) => s.id === strategy)?.source ===
                    STRATEGY_SOURCE_OBJ.USER ? (
                      <>
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" />
                        Clone & Edit
                      </>
                    )}
                  </Button>
                  {strategies.find((s) => s.id === strategy)?.source ===
                    STRATEGY_SOURCE_OBJ.USER && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const s = strategies.find((x) => x.id === strategy);
                        if (!s) return;
                        setEditingStrategy(s);
                        setCloningStrategy(null);
                        setEditorOpen(true);
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Initial Capital
              </label>
              <input
                type="number"
                value={initialCapital}
                onChange={(e) => setInitialCapital(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Max Hold Bars
              </label>
              <input
                type="number"
                value={maxHoldBars}
                onChange={(e) => setMaxHoldBars(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Commission
              </label>
              <input
                type="number"
                step="0.0001"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Slippage
              </label>
              <input
                type="number"
                step="0.0001"
                value={slippage}
                onChange={(e) => setSlippage(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Sizing Mode
              </label>
              <Select
                value={sizingMode}
                onChange={(v) => {
                  const selectedMode = SIZING_MODE.find((mode) => mode === v);
                  if (selectedMode) setSizingMode(selectedMode);
                }}
                ariaLabel="Sizing Mode"
                className="w-full"
                options={SIZING_MODE.map((m) => ({ value: m, label: m }))}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Symbols (comma or add)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={symbolsInput}
                  onChange={(e) => setSymbolsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSymbol();
                    }
                  }}
                  placeholder="e.g. AAPL"
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                />
                <Button variant="outline" onClick={handleAddSymbol}>
                  Add
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {symbols.map((sym) => (
                  <span
                    key={sym}
                    className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs"
                  >
                    {sym}
                    <button
                      onClick={() => handleRemoveSymbol(sym)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Date Range
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                />
              </div>
              {!dateRangeValid && (
                <p className="mt-1 text-xs text-red-500">
                  From date must be before To date
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleRun} disabled={isLoading || !dateRangeValid}>
              <Play className="mr-2 h-4 w-4" />
              Run
            </Button>
            <Button
              variant="secondary"
              onClick={handleOptimize}
              disabled={isOptimizing || symbols.length === 0 || !dateRangeValid}
            >
              <Zap className="mr-2 h-4 w-4" />
              Optimize
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveRun}
              disabled={isSaving || isLoading || !dateRangeValid}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Run
            </Button>
            {results.length > 0 && (
              <Button variant="outline" onClick={clearResults}>
                <Trash2 className="mr-2 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
          {progress !== null && (
            <Progress
              value={progress}
              label="Running backtest..."
              onCancel={() => setProgress(null)}
            />
          )}
          {isOptimizing && (
            <Progress
              value={undefined}
              label="Optimization in progress..."
              onCancel={() => setIsOptimizing(false)}
            />
          )}
          {isLoading && <Loading />}
          {error !== null && <ErrorState message={error} />}
          {saveError !== null && <ErrorState message={saveError} />}
          {optimizeError !== null && <ErrorState message={optimizeError} />}
          {!isLoading &&
            error === null &&
            results.length === 0 &&
            !optimizationResult &&
            !persistedResult && (
              <EmptyState message="No results yet. Select a strategy and click Run, or click Optimize to run grid search." />
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("backtester.optimizationParams")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            {paramGridRows.map((row, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <label className="block text-xs text-muted-foreground">
                    {t("backtester.params.name")}
                  </label>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) =>
                      handleUpdateParamRow(idx, "name", e.target.value)
                    }
                    className="w-full rounded-md border px-2 py-1 text-sm"
                    placeholder="maxHoldBars"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-muted-foreground">
                    {t("backtester.params.min")}
                  </label>
                  <input
                    type="number"
                    value={row.min}
                    onChange={(e) =>
                      handleUpdateParamRow(idx, "min", e.target.value)
                    }
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-muted-foreground">
                    {t("backtester.params.max")}
                  </label>
                  <input
                    type="number"
                    value={row.max}
                    onChange={(e) =>
                      handleUpdateParamRow(idx, "max", e.target.value)
                    }
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-muted-foreground">
                    {t("backtester.params.step")}
                  </label>
                  <input
                    type="number"
                    value={row.step}
                    onChange={(e) =>
                      handleUpdateParamRow(idx, "step", e.target.value)
                    }
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveParamRow(idx)}
                    aria-label={t("backtester.params.remove")}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={handleAddParamRow}>
            <Plus className="mr-1 h-3 w-3" />
            {t("backtester.params.addRow")}
          </Button>
        </CardContent>
      </Card>

      {optimizationResult !== null && (
        <OptimizationResults result={optimizationResult} />
      )}
      {results.length >= 2 && <ComparisonTable results={results} />}
      {results.map((r, i) => (
        <ResultsViewer key={`${r.strategy}-${i}`} result={r} />
      ))}

      {/* Persisted run detail viewer (from "Open" in the runs list) */}
      {detailLoading && <Loading />}
      {detailError !== null && <ErrorState message={detailError} />}
      {persistedResult !== null && (
        <ResultsViewer
          key={`persisted-${detail!.id}`}
          result={persistedResult}
        />
      )}

      {/* Past runs list — persisted backtest runs from the backend */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Past Runs
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {runsLoading && <Loading />}
          {runsError !== null && <ErrorState message={runsError} />}
          {!runsLoading && runsError === null && runs.length === 0 && (
            <EmptyState message="No saved runs yet. Click 'Save Run' to persist a backtest result." />
          )}
          {runs.length > 0 && (
            <div className="space-y-1">
              {runs.map((run) => (
                <div
                  key={run.id}
                  className="flex items-center justify-between rounded-md border p-2 text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {run.strategyId}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDisplayDateTime(new Date(run.createdAt))}
                      {run.symbols.length > 0 &&
                        ` · ${run.symbols.join(", ")}`}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRunId(run.id)}
                      disabled={selectedRunId === run.id}
                    >
                      <FolderOpen className="h-3 w-3 mr-1" />
                      Open
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleDeleteRun(run.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      disabled={deleteRunMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <StrategyEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingStrategy(null);
          setCloningStrategy(null);
        }}
        editing={editingStrategy}
        cloning={cloningStrategy}
        createStrategy={createStrategy}
        updateStrategy={updateStrategy}
        deleteStrategy={deleteStrategy}
        cloneStrategy={cloneStrategy}
        generateStrategyCode={generateStrategyCode}
        onSaved={() => {
          // После сохранения — если создана новая стратегия, выбрать её.
          if (editingStrategy) setStrategy(editingStrategy.id);
        }}
      />
    </div>
  );
}
